import {
    AfterViewInit,
    Component,
    ComponentRef,
    effect,
    Input,
    signal,
    Type,
    ViewChild,
    ViewContainerRef
} from "@angular/core";
import {
    PeriodComparationData,
    PeriodComparationProgressComponent
} from "@shared/components/special-charts/period-comparation-progress/period-comparation-progress.component";
import {
    ITableDreComponent,
    TableDreComponent
} from "@shared/components/special-charts/table-dre/table-dre.component";
import {
    ChartWithInputComponent,
    IChartWithInputComponent
} from "../chart-with-input/chart-with-input.component";
import {
    ISpecialComparationComponent,
    SpecialComparationComponent
} from "../special-comparation/special-comparation.component";
import {
    ChartWithCalculatorComponent,
    IChartWithCalculatorComponent
} from "../chart-with-calculator/chart-with-calculator.component";
import {
    ITableManagementAssetsComponent,
    TableManagementAssetsComponent
} from "../table-management-assets/table-management-assets.component";
import {
    ISpecialChartColumnRangeComponent,
    SpecialChartColumnRangeComponent
} from "../special-chart-column-range/special-chart-column-range.component";
import {
    ISpecialChartColumnComparationComponent,
    SpecialChartColumnComparationComponent
} from "../special-chart-column-comparation/special-chart-column-comparation.component";
import {
    ISpecialChartCoins,
    SpecialChartCoinsComponent
} from "../special-chart-coins/special-chart-coins.component";
import {
    ChartCustomCategoriesComponent,
    IChartCustomCategoriesComponent
} from "../chart-custom-categories/chart-custom-categories.component";
import {
    ISpecialEmployeeCostComponent,
    SpecialEmployeeCostComponent
} from "../special-employee-cost/special-employee-cost.component";
import {
    ISpecialMetricKapiComponent,
    MetricKpiComponent
} from "../metric-kpi/metric-kpi.component";

@Component({
    selector: "app-special-chart-render",
    imports: [],
    standalone: true,
    styleUrl: "./special-chart-render.component.scss",
    template: `
        <div style="height: 100%">
            <ng-container #dynamicComponentContainer></ng-container>
        </div>
    `
})
export class SpecialChartRenderComponent implements AfterViewInit {
    @Input() componentName!: keyof ComponentData;
    @Input() set data(value: ComponentData[keyof ComponentData]) {
        this.dataSignal.set(value);
    }
    private dataSignal = signal<ComponentData[keyof ComponentData]>(null);
    @ViewChild("dynamicComponentContainer", { read: ViewContainerRef })
    private container!: ViewContainerRef;
    private componentMap = new Map<string, Type<any>>([
        ["period-comparation-progress", PeriodComparationProgressComponent],
        ["TABLE_VISUALIZATION", TableDreComponent],
        ["CHART_WITH_INPUT", ChartWithInputComponent],
        ["COMPARATION", SpecialComparationComponent],
        ["CHART_WITH_CALCULATOR", ChartWithCalculatorComponent],
        ["TABLE_MANAGEMENT_ASSETS", TableManagementAssetsComponent],
        ["CHART_COLUMN_RANGE", SpecialChartColumnRangeComponent],
        ["CHART_COLUMN_COMPARATION", SpecialChartColumnComparationComponent],
        ["COINS", SpecialChartCoinsComponent],
        ["CHART_CUSTOM_CATEGORIES", ChartCustomCategoriesComponent],
        ["EMPLOYEE_COST", SpecialEmployeeCostComponent],
        ["METRIC_KPI", MetricKpiComponent]
    ]);

    constructor() {
        effect(() => {
            if (this.dataSignal()) {
                this.loadComponent();
            }
        });
    }

    ngAfterViewInit() {
        this.loadComponent();
    }

    private loadComponent(): ComponentRef<any> {
        if (!this.container) return null;
        this.container.clear();
        const component = this.componentMap.get(this.componentName);
        if (!component) {
            throw new Error(`Component not found: ${this.componentName}`);
        }
        const componentRef = this.container.createComponent(component);
        Object.assign(componentRef.instance, this.dataSignal());
        return componentRef;
    }
}

type ComponentData = {
    "period-comparation-progress": PeriodComparationData;
    TABLE_VISUALIZATION: ITableDreComponent;
    CHART_WITH_INPUT: IChartWithInputComponent;
    COMPARATION: ISpecialComparationComponent;
    CHART_WITH_CALCULATOR: IChartWithCalculatorComponent;
    TABLE_MANAGEMENT_ASSETS: ITableManagementAssetsComponent;
    CHART_COLUMN_RANGE: ISpecialChartColumnRangeComponent;
    CHART_COLUMN_COMPARATION: ISpecialChartColumnComparationComponent;
    COINS: ISpecialChartCoins;
    CHART_CUSTOM_CATEGORIES: IChartCustomCategoriesComponent;
    EMPLOYEE_COST: ISpecialEmployeeCostComponent;
    METRIC_KPI: ISpecialMetricKapiComponent;
};
